import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, HeartIcon, CogIcon, BanknotesIcon, HandRaisedIcon, CurrencyDollarIcon, AcademicCapIcon, ShieldCheckIcon, ChevronDownIcon, BookOpenIcon, EnvelopeIcon, QuestionMarkCircleIcon, ChatBubbleLeftRightIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import LogoImage from './shared/LogoImage';
import TopQuickMenu from './TopQuickMenu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { useIsMobile } from '@/hooks/use-mobile';
const createSafeEarlyAdopterHandler = (openModal: (source?: string) => void, source: string) => {
  return () => {
    try {
      openModal(source);
    } catch (error) {}
  };
};
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const {
    openModal
  } = useEarlyAdopter();

  // Close menu on route change
  useEffect(() => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [location.pathname]);
  const navItems = [{
    href: '/older-adults',
    label: 'Older Adults',
    icon: HeartIcon
  }, {
    href: '/features',
    label: 'Features',
    icon: CogIcon
  }, {
    href: '/pricing',
    label: 'Pricing',
    icon: BanknotesIcon
  }];
  
  const familyItems = [{
    href: '/thrivefamily',
    label: 'ThriveFamily',
    icon: UserGroupIcon
  }, {
    href: '/family-caregiver-tools',
    label: 'ThriveFamily+',
    icon: CogIcon
  }, {
    href: '/caregiver-coordination',
    label: 'ThriveCare',
    icon: ShieldCheckIcon
  }];
  const resourceItems = [{
    href: '/aging-in-place',
    label: 'Aging in Place Guide',
    icon: BookOpenIcon
  }, {
    href: '/about-seniorthrive',
    label: 'About SeniorThrive',
    icon: HeartIcon
  }, {
    href: '/stories',
    label: 'Stories',
    icon: ChatBubbleLeftRightIcon
  }, {
    href: '/blog',
    label: 'Blog',
    icon: BookOpenIcon
  }, {
    href: '/support',
    label: 'Support',
    icon: QuestionMarkCircleIcon
  }, {
    href: '/support/faq',
    label: 'FAQs',
    icon: QuestionMarkCircleIcon
  }];
  const portalItems = [{
    href: '/professional-network',
    label: 'Add Your Expertise to the Home OS',
    description: 'Join our directory of vetted experts and connect with families who need your specific expertise.',
    actionLabel: 'Start Your Application',
    icon: UserGroupIcon
  }, {
    href: '/partners',
    label: 'Strategic Integrations',
    description: 'Co-brand, integrate data, and build the future of aging-in-place technology with us.',
    actionLabel: 'Explore Partnerships',
    icon: HandRaisedIcon
  }, {
    href: '/sponsor-older-adults',
    label: 'Sponsorships',
    description: 'Extend SeniorThrive access to clients, members, or communities you support.',
    actionLabel: 'View Sponsorship Opportunities',
    icon: CurrencyDollarIcon
  }];
  const isActive = (href: string) => location.pathname === href;
  return <>
    {/* Top Bar */}
    <div className="bg-primary text-white py-2 px-4">
      <div className="container mx-auto text-center">
        <p className="text-sm font-medium">
          SeniorThrive brings older adults, families, and caregivers into one circle of support
        </p>
      </div>
    </div>
    
    {/* Quick Access Menu */}
    <TopQuickMenu />
    
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Enhanced Logo */}
          <Link to="/" className="flex items-center group mr-8 flex-shrink-0">
            <LogoImage />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {/* Main Navigation Links */}
            <Link to="/older-adults" className={cn("px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-300", isActive('/older-adults') ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-gray-100")}>
              Older Adults
            </Link>

            {/* Families Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={cn("flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-300", familyItems.some(item => isActive(item.href)) ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-gray-100")}>
                  Families
                  <ChevronDownIcon className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[250px] p-2 bg-white border shadow-xl z-50" align="start">
                {familyItems.map(item => {
                  const IconComponent = item.icon;
                  return <DropdownMenuItem key={item.href} asChild>
                      <Link to={item.href} className={cn("flex items-center gap-3 w-full px-3 py-2 rounded-md transition-colors cursor-pointer", isActive(item.href) ? "bg-accent/50 text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground")}>
                        <IconComponent className="h-4 w-4 text-muted-foreground" />
                        <div className="text-sm font-medium">{item.label}</div>
                      </Link>
                    </DropdownMenuItem>;
                })}
              </DropdownMenuContent>
            </DropdownMenu>


            {/* Professionals Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={cn("flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-300", portalItems.some(item => isActive(item.href)) ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-gray-100")}>
                  Professionals
                  <ChevronDownIcon className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[420px] p-2 bg-white border shadow-xl z-50" align="start">
                {portalItems.map(item => {
                  const IconComponent = item.icon;
                  return <DropdownMenuItem key={item.href} asChild>
                      <Link to={item.href} className={cn("flex items-start gap-3 w-full px-3 py-3 rounded-md transition-colors cursor-pointer group", isActive(item.href) ? "bg-accent/50 text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground")}>
                        <IconComponent className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="text-sm font-medium mb-1">{item.label}</div>
                          <div className="text-xs text-muted-foreground leading-relaxed mb-2">{item.description}</div>
                          <div className="text-xs font-semibold text-primary group-hover:underline">{item.actionLabel} →</div>
                        </div>
                      </Link>
                    </DropdownMenuItem>;
                })}
              </DropdownMenuContent>
            </DropdownMenu>


            <Link to="/features" className={cn("px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-300", isActive('/features') ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-gray-100")}>Features</Link>

            <Link to="/pricing" className={cn("px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-300", isActive('/pricing') ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-gray-100")}>
              Pricing
            </Link>

            {/* Resources Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={cn("flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-300", resourceItems.some(item => isActive(item.href)) ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-gray-100")}>
                  Resources
                  <ChevronDownIcon className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[250px] p-2 bg-white border shadow-xl z-50" align="start">
                {resourceItems.map(item => {
                  const IconComponent = item.icon;
                  return <DropdownMenuItem key={item.href} asChild>
                      <Link to={item.href} className={cn("flex items-center gap-3 w-full px-3 py-2 rounded-md transition-colors cursor-pointer", isActive(item.href) ? "bg-accent/50 text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground")}>
                        <IconComponent className="h-4 w-4 text-muted-foreground" />
                        <div className="text-sm font-medium">{item.label}</div>
                      </Link>
                    </DropdownMenuItem>;
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center space-x-3 ml-8">
            <Button size="lg" className="bg-white text-primary border-2 border-primary/20 hover:border-primary hover:bg-primary/5 font-semibold px-6 transition-all duration-200" onClick={createSafeEarlyAdopterHandler(openModal, 'Header - Login')}>
              Login
            </Button>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-semibold px-6 shadow-lg hover:shadow-xl transition-all duration-200" onClick={createSafeEarlyAdopterHandler(openModal, 'Header - Get Started')}>
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Drawer */}
          <Drawer open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DrawerTrigger asChild>
              <button className="md:hidden p-2" aria-label="Toggle menu">
                <Bars3Icon className="w-6 h-6" />
              </button>
            </DrawerTrigger>
            <DrawerContent className="max-h-[90vh]">
              <DrawerHeader className="text-left">
                <DrawerTitle className="flex items-center gap-2">
                  <LogoImage />
                </DrawerTitle>
              </DrawerHeader>
              
              <div className="px-4 pb-6 overflow-y-auto">
                <nav className="space-y-6">
                  {/* Families Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3 px-2">Families</h3>
                    <div className="space-y-1">
                      {familyItems.map(item => {
                        const IconComponent = item.icon;
                        return <Link key={item.href} to={item.href} className={cn("flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors", isActive(item.href) ? "bg-primary/10 text-primary border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-accent")} onClick={() => setIsMenuOpen(false)}>
                            <IconComponent className="h-5 w-5" />
                            {item.label}
                          </Link>;
                      })}
                    </div>
                  </div>

                  {/* Main Navigation */}
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3 px-2">Main Navigation</h3>
                    <div className="space-y-1">
                      {navItems.map(item => {
                        const IconComponent = item.icon;
                        return <Link key={item.href} to={item.href} className={cn("flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors", isActive(item.href) ? "bg-primary/10 text-primary border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-accent")} onClick={() => setIsMenuOpen(false)}>
                            <IconComponent className="h-5 w-5" />
                            {item.label}
                          </Link>;
                      })}
                    </div>
                  </div>


                  {/* Professionals Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3 px-2">Professionals</h3>
                    <div className="space-y-1">
                      {portalItems.map(item => {
                        const IconComponent = item.icon;
                        return <Link key={item.href} to={item.href} className={cn("flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors", isActive(item.href) ? "bg-primary/10 text-primary border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-accent")} onClick={() => setIsMenuOpen(false)}>
                            <IconComponent className="h-5 w-5" />
                            {item.label}
                          </Link>;
                      })}
                    </div>
                  </div>

                  {/* Resources Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-3 px-2">Resources</h3>
                    <div className="space-y-1">
                      {resourceItems.map(item => {
                        const IconComponent = item.icon;
                        return <Link key={item.href} to={item.href} className={cn("flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors", isActive(item.href) ? "bg-primary/10 text-primary border border-primary/20" : "text-muted-foreground hover:text-foreground hover:bg-accent")} onClick={() => setIsMenuOpen(false)}>
                            <IconComponent className="h-5 w-5" />
                            {item.label}
                          </Link>;
                      })}
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="space-y-3 pt-4 border-t">
                    <Button className="w-full bg-login hover:bg-login/90 text-login-foreground font-semibold py-3" onClick={() => {
                      createSafeEarlyAdopterHandler(openModal, 'Mobile Menu - Login')();
                      setIsMenuOpen(false);
                    }}>
                      Login
                    </Button>
                    <Button className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3" onClick={() => {
                      createSafeEarlyAdopterHandler(openModal, 'Mobile Menu - Get Started')();
                      setIsMenuOpen(false);
                    }}>
                      Get Started
                    </Button>
                  </div>
                </nav>
              </div>
            </DrawerContent>
          </Drawer>
        </div>
      </div>
    </header>
  </>;
};
export default Header;